import { TIMEOUT } from "./common"

type FetchConfig = {
  url: string
  method: string
  headers?: HeadersInit
  body?: any
} & NetConfig

type TOperation = {
  [key: string]: {
    headers?: { [key: string]: any }
    formatting: (params: any) => any
  }
}

/**
 * 根据requestType设置headers值，格式body数据
 */
const dataOperation: TOperation = {
  json: {
    headers: { "content-type": "application/json" },
    formatting(params = "") {
      if (isJSONSerializable(params)) {
        let result =
          typeof params === "string" ? params : JSON.stringify(params)
        return result
      }
    },
  },
  formdata: {
    // headers: { 'Content-Type': 'multipart/form-data' }, // application/x-www-form-urlencoded
    formatting(params = {}) {
      let _formData = new FormData()
      for (const key of Object.keys(params)) {
        if (key === "uploadFile") {
          const files = params["uploadFile"]
          if (!files?.length) continue
          for (const file of files) {
            _formData.append("uploadFile", file, file.name)
          }
        } else {
          _formData.append(key, params[key])
        }
      }
      return _formData
    },
  },
}
/**
 * 判断data是否可以序列化
 * @param data
 */
function isJSONSerializable(data: any) {
  if (data === undefined) return false
  const t = typeof data
  if (t === "string" || t === "number" || t === "boolean" || t === null)
    return true
  if (t !== "object") return false
  if (Array.isArray(data)) return true
  return data?.constructor?.name === "Object"
}

const _fetch = {
  send<T = any>(config: FetchConfig): Promise<NetResult<T>> {
    const cancelRequest = new AbortController()
    const { url, responseType = "json", timeout = TIMEOUT } = config
    const abortControllerFetch: Promise<NetResult<any>> = new Promise(
      async (resolve, reject) => {
        try {
          const response = await fetch(url, {
            ...config,
            signal: cancelRequest.signal,
          })
          const { status, headers } = response
          const body = response.ok ? await response[responseType]() : null
          resolve({ headers, status, body, url })
        } catch (e) {
          reject(e)
        }
      }
    )
    const time: Promise<NetResult<T>> = new Promise((resolve, reject) => {
      setTimeout(() => {
        cancelRequest.abort()
        reject({
          status: 504,
          statusText: "timeout",
        })
      }, timeout)
    })

    return Promise.race([abortControllerFetch, time])
  },
  async preSend<T = any>(
    config: NetConfig & { url: string; method?: string }
  ): Promise<NetResult<T>> {
    let {
      requestType = "json",
      headers = {},
      data = {},
      method = "GET",
    } = config
    if (!["json", "formdata"].includes(requestType)) requestType = "json"
    const _config: FetchConfig = {
      ...config,
      method,
      headers: {
        ...dataOperation[requestType]?.headers,
        ...headers,
      },
    }
    if (method !== "GET") {
      _config.body = dataOperation[requestType].formatting(data)
    } else {
      delete _config.data
    }
    return _fetch.send({ method, ..._config })
  },

  get<T = any>(config: NetConfig & { url: string }): Promise<NetResult<T>> {
    return _fetch.preSend({ ...config, method: "GET" })
  },

  post<T = any>(config: NetConfig & { url: string }): Promise<NetResult<T>> {
    return _fetch.preSend({ ...config, method: "POST" })
  },
  /**
   * 上传
   * @param url 请求地址
   * @param config 配置
   */
  async upload<T = any>(
    config?: NetConfig & {
      url?: string
      method?: string
      fileList?: any[]
      formData?: any
    }
  ): Promise<NetResult<T>> {
    config.method = "POST"
    const { data = {} } = config
    const _config: NetConfig & { url: string; method?: string } = {
      ...config,
      url: config?.url
        ? config.url
        : "http://openapi.sunmi.com/v3/resource/file/batchUpload",
      requestType: "formdata",
      data: { ...data, uploadFile: config.fileList },
    }
    const result = await _fetch.preSend({ ..._config })
    return result
  },

  async download(
    config?: NetConfig & { url: string; name?: string }
  ): Promise<void> {
    // 直接下载
    // window.open(url)
    // blob下载
    // config.responseType = 'blob'

    const { body } = await _fetch.post({ ...config })
    return downBlob(body)
  },

  async put<T = any>(
    config?: NetConfig & {
      url: string
      fileList?: any[]
      formData?: any
    }
  ): Promise<NetResult<T>> {
    const { data = {}, fileList = [] } = config || {}
    if (fileList?.length) {
      config = {
        requestType: "formdata",
        ...config,
      }
      data["fileList"] = fileList
    }
    const result = await _fetch.preSend({ data, ...config, method: "PUT" })
    return result
  },

  async delete<T = any>(
    config?: NetConfig & { url: string }
  ): Promise<NetResult<T>> {
    const result = await _fetch.preSend({ ...config, method: "DELETE" })
    return result
  },
}

export default _fetch
/**
 * 下载blob文件
 * @param blob
 */
export function downBlob(blob: Blob) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.setAttribute("download", url)
  a.href = url
  a.style.display = "none"
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
